/**
 * Kézifegyver-modell — a képernyő alján látszó fegyver.
 *
 * Ez az a modell, amit a játékos a játékidő 100%-ában néz, ezért a
 * részletessége aránytalanul sokat számít az összbenyomásban. Minden
 * fegyver kódból épül, közös stílusban, de eltérő tömeggel és sziluettel:
 * a pisztoly könnyed és apró, a rakétavető nehéz és eltakarja a fél képet.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import type { ViewModelApi } from '../core/contracts'
import { RAMPS } from '../assets/palette'
import { damp } from '../core/math'

const matCache = new Map<string, THREE.MeshLambertMaterial | THREE.MeshBasicMaterial>()

function m(color: number, emissive = false): THREE.Material {
  const key = `${color}:${emissive}`
  let mat = matCache.get(key)
  if (!mat) {
    mat = emissive
      ? new THREE.MeshBasicMaterial({ color, toneMapped: false })
      : new THREE.MeshLambertMaterial({ color, flatShading: true })
    matCache.set(key, mat)
  }
  return mat
}

function part(
  w: number,
  h: number,
  d: number,
  x: number,
  y: number,
  z: number,
  color: number,
  emissive = false,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m(color, emissive))
  mesh.position.set(x, y, z)
  return mesh
}

function tube(r: number, len: number, x: number, y: number, z: number, color: number, sides = 8): THREE.Mesh {
  const g = new THREE.CylinderGeometry(r, r, len, sides)
  g.rotateX(Math.PI / 2)
  const mesh = new THREE.Mesh(g, m(color))
  mesh.position.set(x, y, z)
  return mesh
}

export interface WeaponVisual {
  root: THREE.Group
  /** A torkolat helye a modell lokális terében. */
  muzzle: THREE.Vector3
  /** Visszarúgás-erősítő: a nehéz fegyver nagyobbat rúg. */
  recoilScale: number
  /** Elsütéskor mozgó alkatrészek (szán, forgócső). */
  animate?(t: number, fireAge: number): void
}

const R = RAMPS

const BUILDERS: Record<string, () => WeaponVisual> = {
  pistol: () => {
    const root = new THREE.Group()
    const dark = R.stone[2]
    const body = R.stone[4]
    const hilite = R.stone[6]

    // Szán: sötét alaptest, tetején világosabb bordával — a két árnyalat
    // adja meg a formát ott, ahol a lapos megvilágítás önmagában nem tudná.
    const slide = new THREE.Group()
    slide.add(part(0.085, 0.075, 0.3, 0, 0, 0, body))
    slide.add(part(0.09, 0.016, 0.3, 0, 0.042, 0, hilite))
    // Szánhúzó rovátkák hátul
    for (let i = 0; i < 5; i++) {
      slide.add(part(0.092, 0.05, 0.008, 0, 0, 0.1 - i * 0.017, dark))
    }
    // Kivetőnyílás
    slide.add(part(0.095, 0.03, 0.06, 0.02, 0.012, -0.03, dark))
    slide.position.set(0, 0.035, -0.15)
    root.add(slide)

    // Váz és sátorvas
    root.add(part(0.075, 0.05, 0.22, 0, -0.02, -0.09, dark))
    root.add(part(0.05, 0.012, 0.09, 0, -0.075, -0.045, dark))
    root.add(part(0.012, 0.055, 0.012, 0, -0.05, -0.088, dark))
    root.add(part(0.02, 0.045, 0.012, 0, -0.048, -0.02, hilite))

    // Markolat: enyhén hátradöntve, bordázott felülettel
    const grip = new THREE.Group()
    grip.add(part(0.07, 0.19, 0.1, 0, -0.09, 0.015, body))
    for (let i = 0; i < 4; i++) {
      grip.add(part(0.074, 0.012, 0.095, 0, -0.05 - i * 0.035, 0.015, dark))
    }
    grip.rotation.x = -0.24
    root.add(grip)

    // Cső és irányzékok
    root.add(tube(0.019, 0.1, 0, 0.035, -0.32, dark, 10))
    root.add(part(0.014, 0.026, 0.016, 0, 0.085, -0.275, hilite))
    root.add(part(0.055, 0.024, 0.018, 0, 0.085, -0.02, dark))
    root.add(part(0.014, 0.02, 0.02, 0, 0.09, -0.02, R.hazard[6], true))

    return {
      root,
      muzzle: new THREE.Vector3(0, 0.035, -0.38),
      recoilScale: 0.7,
      animate(_t, fireAge) {
        // A szán hátracsapódik és visszatér.
        const k = Math.max(0, 1 - fireAge / 0.09)
        slide.position.z = -0.15 + k * 0.075
      },
    }
  },

  shotgun: () => {
    const root = new THREE.Group()
    root.add(part(0.13, 0.11, 0.5, 0, 0.0, -0.2, R.rust[5]))
    root.add(tube(0.033, 0.62, -0.032, 0.05, -0.34, R.stone[4]))
    root.add(tube(0.033, 0.62, 0.032, 0.05, -0.34, R.stone[4]))
    const pump = part(0.11, 0.08, 0.16, 0, -0.02, -0.3, R.rust[5])
    root.add(pump)
    root.add(part(0.1, 0.2, 0.14, 0, -0.12, 0.06, R.rust[6]))
    return {
      root,
      muzzle: new THREE.Vector3(0, 0.05, -0.66),
      recoilScale: 1.6,
      animate(_t, fireAge) {
        // Pumpálás: a lövés UTÁN, nem közben — ez adja a fegyver ritmusát.
        const p = fireAge - 0.22
        if (p > 0 && p < 0.34) {
          const k = Math.sin((p / 0.34) * Math.PI)
          pump.position.z = -0.3 + k * 0.14
        } else {
          pump.position.z = -0.3
        }
      },
    }
  },

  chaingun: () => {
    const root = new THREE.Group()
    root.add(part(0.17, 0.15, 0.34, 0, -0.02, -0.06, R.stone[5]))
    const barrels = new THREE.Group()
    barrels.position.set(0, 0.02, -0.32)
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2
      barrels.add(tube(0.019, 0.44, Math.cos(a) * 0.05, Math.sin(a) * 0.05, 0, R.stone[5]))
    }
    root.add(barrels)
    root.add(part(0.06, 0.06, 0.06, 0, 0.02, -0.32, R.stone[5]))
    root.add(part(0.11, 0.19, 0.13, 0, -0.15, 0.05, R.stone[4]))
    // Lőszerheveder — vizuálisan elárulja, hogy „eszik".
    for (let i = 0; i < 6; i++) {
      root.add(part(0.03, 0.05, 0.03, 0.1, -0.1 - i * 0.045, 0.02 + i * 0.012, R.hazard[5]))
    }
    return {
      root,
      muzzle: new THREE.Vector3(0, 0.02, -0.56),
      recoilScale: 0.5,
      animate(_t, fireAge) {
        // A cső csak lövés közben pörög, utána lassul — nem örökmozgó.
        const spin = Math.max(0, 1 - fireAge / 0.35)
        barrels.rotation.z += spin * 0.55
      },
    }
  },

  rocket: () => {
    const root = new THREE.Group()
    root.add(tube(0.075, 0.72, 0, 0.02, -0.24, R.stone[5], 10))
    root.add(part(0.2, 0.05, 0.16, 0, 0.11, -0.1, R.stone[4]))
    root.add(part(0.1, 0.19, 0.13, 0, -0.14, 0.06, R.rust[5]))
    // Célzókeret — a nagy fegyver „pontatlanságát" ellensúlyozza vizuálisan.
    root.add(part(0.005, 0.07, 0.005, -0.05, 0.13, -0.4, R.hazard[6], true))
    root.add(part(0.005, 0.07, 0.005, 0.05, 0.13, -0.4, R.hazard[6], true))
    const warhead = part(0.07, 0.07, 0.12, 0, 0.02, -0.5, 0xff3b1f, true)
    root.add(warhead)
    return {
      root,
      muzzle: new THREE.Vector3(0, 0.02, -0.62),
      recoilScale: 2.2,
      animate(_t, fireAge) {
        // Az újratöltött rakéta csak a ciklus végén jelenik meg újra.
        warhead.visible = fireAge > 0.65
      },
    }
  },

  plasma: () => {
    const root = new THREE.Group()
    root.add(part(0.15, 0.13, 0.42, 0, 0, -0.16, R.tech[5]))
    root.add(tube(0.045, 0.3, 0, 0.02, -0.42, R.tech[4], 8))
    const coreGlow = part(0.06, 0.06, 0.22, 0, 0.02, -0.2, 0x8affff, true)
    root.add(coreGlow)
    root.add(part(0.1, 0.19, 0.13, 0, -0.13, 0.05, R.tech[3]))
    // Hűtőbordák
    for (let i = 0; i < 4; i++) {
      root.add(part(0.19, 0.015, 0.04, 0, 0.075, -0.05 - i * 0.08, R.tech[4]))
    }
    const ring = part(0.11, 0.11, 0.03, 0, 0.02, -0.54, 0x64c4cf, true)
    root.add(ring)
    return {
      root,
      muzzle: new THREE.Vector3(0, 0.02, -0.58),
      recoilScale: 0.55,
      animate(t, fireAge) {
        // A mag folyamatosan lüktet, lövéskor felizzik — „él" a fegyver.
        const idle = 0.85 + Math.sin(t * 5) * 0.15
        const heat = Math.max(0, 1 - fireAge / 0.25)
        coreGlow.scale.setScalar(idle + heat * 0.9)
        ring.scale.setScalar(1 + heat * 0.5)
      },
    }
  },

  fist: () => {
    const root = new THREE.Group()
    root.add(part(0.16, 0.14, 0.22, 0.02, -0.05, -0.1, R.flesh[6]))
    root.add(part(0.13, 0.11, 0.16, 0.02, -0.06, 0.06, R.flesh[5]))
    for (let i = 0; i < 4; i++) {
      root.add(part(0.035, 0.05, 0.06, -0.04 + i * 0.038, 0.02, -0.19, R.stone[6]))
    }
    return { root, muzzle: new THREE.Vector3(0, 0, -0.25), recoilScale: 1.1 }
  },
}

/** Alapállás: jobb alsó sarok, enyhén befelé fordítva. */
const REST = new THREE.Vector3(0.245, -0.26, -0.78)

/**
 * Alap-elfordulás. Ha a fegyver pontosan előre néz, hátulról csak a szán
 * végét látni — lapos, olvashatatlan folt. Enyhén befelé és lefelé fordítva
 * a sziluett háromnegyedes nézetből olvasódik, ahogy minden FPS-ben.
 */
const REST_ROT = new THREE.Euler(0.035, 0.185, -0.045)

export class ViewModel implements ViewModelApi {
  readonly group = new THREE.Group()

  /**
   * A fegyver külön jelenetben él, origóban álló kamerával (hogy sose lógjon
   * falba). Emiatt a modell lokális koordinátái a kamera TÉRÉBEN vannak —
   * a valódi világpozícióhoz hozzá kell adni a kamera helyét. Ezt a
   * Renderer tartja karban minden kereten.
   */
  readonly worldOffset = new THREE.Vector3()
  private current: WeaponVisual | null = null
  private currentId = ''
  private pendingId: string | null = null

  private recoil = 0
  private recoilVel = 0
  private fireAge = 10
  private bobPhase = 0
  private bobAmount = 0
  private swayX = 0
  private swayY = 0
  private lowerT = 0
  private time = 0

  constructor() {
    this.group.name = 'viewmodel'
    // Kissé kisebb lépték: közelről minden kódból épített modell tömbösnek
    // hat, távolabbról viszont olvashatóvá válik a sziluettje.
    this.group.scale.setScalar(0.8)
    // A fegyver soha ne vágódjon bele a falba: külön rétegen, saját
    // kamerával rendereljük (lásd Renderer).
    this.group.position.copy(REST)
  }

  equip(weaponId: string): void {
    if (weaponId === this.currentId && this.lowerT < 0.05) return
    if (this.currentId === '') {
      this.swap(weaponId)
      this.lowerT = 1
      return
    }
    this.pendingId = weaponId
  }

  private swap(weaponId: string): void {
    if (this.current) {
      this.group.remove(this.current.root)
      this.current.root.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose()
      })
    }
    const build = BUILDERS[weaponId] ?? BUILDERS.pistol
    this.current = build()
    this.currentId = weaponId
    this.group.add(this.current.root)
  }

  fire(recoilAmount: number): void {
    const scale = this.current?.recoilScale ?? 1
    this.recoilVel += recoilAmount * scale * 5.5
    this.fireAge = 0
  }

  muzzleWorld(out: THREE.Vector3): THREE.Vector3 {
    if (!this.current) return out.copy(this.worldOffset)
    out.copy(this.current.muzzle)
    this.current.root.localToWorld(out)
    return out.add(this.worldOffset)
  }

  get lowered(): number {
    return this.lowerT
  }

  get weaponId(): string {
    return this.currentId
  }

  /**
   * Keretenkénti frissítés.
   * @param speed a játékos vízszintes sebessége
   * @param lookDeltaX az egérmozgás X-e (a fegyver „utána lendül")
   */
  update(dt: number, speed: number, onGround: boolean, lookDeltaX: number, lookDeltaY: number): void {
    this.time += dt
    this.fireAge += dt

    // --- Fegyverváltás: lehúzás → csere → felemelés ---
    if (this.pendingId !== null) {
      this.lowerT = Math.min(1, this.lowerT + dt * 7)
      if (this.lowerT >= 1) {
        this.swap(this.pendingId)
        this.pendingId = null
      }
    } else {
      this.lowerT = Math.max(0, this.lowerT - dt * 7)
    }

    // --- Visszarúgás: rugó-csillapítás ---
    this.recoilVel -= this.recoil * 140 * dt
    this.recoilVel *= Math.max(0, 1 - 13 * dt)
    this.recoil += this.recoilVel * dt
    this.recoil = Math.max(-0.02, Math.min(0.2, this.recoil))

    // --- Járásbólogatás ---
    const targetBob = onGround ? Math.min(1, speed / 8) : 0
    this.bobAmount = damp(this.bobAmount, targetBob, 0.02, dt)
    this.bobPhase += dt * 9.2 * Math.max(0.35, targetBob)

    // --- Késleltetett lendület: a fegyver „utána húz" a nézésnek ---
    this.swayX = damp(this.swayX, -lookDeltaX * 0.55, 0.001, dt)
    this.swayY = damp(this.swayY, -lookDeltaY * 0.45, 0.001, dt)
    this.swayX = Math.max(-0.11, Math.min(0.11, this.swayX))
    this.swayY = Math.max(-0.09, Math.min(0.09, this.swayY))

    const bobX = Math.cos(this.bobPhase) * 0.028 * this.bobAmount
    const bobY = -Math.abs(Math.sin(this.bobPhase)) * 0.026 * this.bobAmount
    // Enyhe „lebegés" álláskor — az abszolút mozdulatlanság élettelen.
    const idleY = Math.sin(this.time * 1.6) * 0.006 * (1 - this.bobAmount)

    this.group.position.set(
      REST.x + bobX + this.swayX,
      REST.y + bobY + idleY + this.swayY - this.lowerT * 0.75,
      REST.z + this.recoil * 0.55,
    )
    this.group.rotation.set(
      REST_ROT.x + this.recoil * 1.9 + this.swayY * 1.2,
      REST_ROT.y + this.swayX * 1.4,
      REST_ROT.z + this.swayX * 0.9 + this.bobAmount * Math.sin(this.bobPhase) * 0.02,
    )

    this.current?.animate?.(this.time, this.fireAge)
  }

  /** A jelenleg fogott fegyver geometriájának felszabadítása. */
  dispose(): void {
    if (!this.current) return
    this.group.remove(this.current.root)
    this.current.root.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose()
    })
    this.current = null
    this.currentId = ''
  }

  reset(): void {
    this.recoil = 0
    this.recoilVel = 0
    this.fireAge = 10
    this.lowerT = 0
    this.pendingId = null
  }
}
